import { z } from 'zod';

/**
 * **Os cometas: o primeiro recurso do mundo aberto.**
 *
 * ## O que um cometa é
 *
 * Uma rocha inerte feita de PARTES. Ela não ataca, não persegue, não colide
 * como uma nave e não tem nada que se possa envenenar, sangrar, curar ou
 * refletir. O que ela tem é vida repartida por pedaços que se partem um a um, e
 * uma silhueta que muda à medida que os perde.
 *
 * ## O que este ficheiro decide, e o que deliberadamente não decide
 *
 * Aqui está o **contrato**: os relógios, a chance, a forma pública e as
 * derivações de identidade. Não está aqui quanta vida uma parte tem, quantas
 * partes existem nem quanto ouro um golpe paga — isso é equilíbrio, vive em
 * `open-world/room/balance.ts`, e é provisório até ao benchmark final.
 *
 * ## O segredo
 *
 * A rolagem de trinta por cento e a definição da joia **nunca atravessam o
 * protocolo**. Antes da revelação, um cometa com joia e um cometa sem joia são
 * indistinguíveis para o cliente: o que vai na trama é a mesma coisa nos dois
 * casos. Depois da revelação viaja a EXISTÊNCIA de um núcleo e a vida dele, e
 * nem então a definição — o jogador descobre qual joia era quando ela entra na
 * carga, que é o instante em que ela passa a ser dele.
 *
 * É por isso que a revelação não é uma consequência da rolagem: um cometa
 * revela o núcleo por ter perdido partes suficientes, tenha joia ou não. Um
 * núcleo vazio é um núcleo que se parte e não dá nada, e é a única maneira de a
 * revelação não ser, ela própria, um anúncio.
 */

/* -------------------------------------------------------------------------- */
/*  Os relógios                                                               */
/* -------------------------------------------------------------------------- */

/**
 * **Trinta minutos sem levar dano, e o cometa PARCIALMENTE danificado
 * reconstrói-se.**
 *
 * Este é o primeiro dos dois relógios, e o que ele devolve é o corpo — não uma
 * geração. A rocha volta a estar inteira, com o selo que já tinha e a joia
 * ainda por levar, e **nada é re-rolado**: reconstruir não é renascer, e uma
 * segunda rolagem aqui daria de graça a quem esperasse meia hora.
 *
 * Tempo de RELÓGIO e não tempo observado pela sala: um quadrante vazio não
 * corre tiques, e um cometa que só se reconstruísse enquanto alguém o estivesse
 * a ver seria um cometa que nunca se reconstrói. O que é guardado é o instante
 * do último dano, em milissegundos absolutos, e a comparação faz-se contra o
 * relógio da parede sempre que a sala acorda.
 */
export const OPEN_WORLD_COMET_REBUILD_IDLE_MS = 30 * 60 * 1_000;

/**
 * **Trinta minutos depois de COMPLETAMENTE destruído, e nasce outro.**
 *
 * O segundo relógio, e o outro evento. Aqui é uma GERAÇÃO nova: identidade
 * pública nova, todas as partes inteiras, distribuição determinística de Ouro
 * nova, e uma rolagem secreta nova dos trinta por cento. O que ficou para trás
 * não volta — a joia daquela geração, se existiu e foi levada, é de quem a
 * levou; se existiu e não foi levada, morreu com a geração que a continha, e
 * nem dano, nem autoria, nem recompensa atravessam para a nova.
 *
 * ## Porque é o MESMO prazo do outro relógio, e continuam a ser dois
 *
 * **Decisão do dono** (gate V28I.U): um cometa completamente destruído reaparece
 * totalmente reconstruído após trinta minutos, e não existe uma espera adicional
 * de uma hora. Era uma hora, e a hora foi removida.
 *
 * Terem o mesmo número não os torna o mesmo relógio, e juntá-los seria o erro:
 * eles contam a partir de instantes diferentes — este a partir da destruição
 * total, o outro a partir do último dano — e produzem resultados diferentes.
 * Confundi-los daria uma rolagem de joia a um cometa que só estava arranhado.
 *
 * O prazo é absoluto e persistido em `respawnAtMs`, portanto sobrevive à
 * reconstrução do objeto durável e à ausência de jogadores: a primeira
 * observação depois de ele vencer é a que faz nascer a geração seguinte.
 */
export const OPEN_WORLD_COMET_RESPAWN_MS = 30 * 60 * 1_000;

/**
 * A chance de um cometa conter uma joia, em pontos base. **Trinta por cento.**
 *
 * Do contrato aprovado, e por isso está aqui e não no equilíbrio provisório:
 * este número foi decidido, os outros não. Sorteado uma vez por geração, com
 * entropia secreta do mundo, e o resultado nunca atravessa o protocolo.
 */
export const OPEN_WORLD_COMET_JEWEL_CHANCE_BPS = 3_000;

/**
 * O nível de uma joia que sai de um cometa: **um**, sempre.
 *
 * Do contrato. Subir níveis é fundir cópias, e fundir é uma coisa que o jogador
 * faz — um cometa que entregasse nível dois estaria a fazê-lo por ele.
 */
export const OPEN_WORLD_COMET_JEWEL_LEVEL = 1;

/* -------------------------------------------------------------------------- */
/*  A identidade                                                              */
/* -------------------------------------------------------------------------- */

/**
 * O separador das identidades derivadas. Proibido dentro de um `worldId` e
 * ausente de um uuid, portanto duas derivações não podem colidir numa só.
 */
const SEPARATOR = '|';

/**
 * **O id de uma recompensa de ouro**, derivado e nunca cunhado.
 *
 * A sala pede à autoridade económica que credite ouro a uma conta; o pedido
 * pode falhar depois de o crédito ter acontecido, e a sala repete. Um id
 * aleatório obrigaria a sala a escrevê-lo antes de perguntar e a lembrá-lo
 * através de uma evicção. Um id DERIVADO é o mesmo em cada tentativa, porque é
 * uma função do que já aconteceu: este mundo, este cometa, esta geração, esta
 * conta, e o ordinal do lote.
 *
 * O ordinal é o que permite AGRUPAR sem perder parcelas: a sala acumula o ouro
 * de vários golpes num lote, fecha-o, e o lote seguinte é outro ordinal. Nenhum
 * lote confirmado se perde, e repetir um lote não credita duas vezes.
 */
export function openWorldCometGoldEventId(input: {
  readonly worldId: string;
  readonly cometId: string;
  readonly generation: number;
  readonly accountId: string;
  readonly batch: number;
}): string {
  return [
    'comet-gold',
    input.worldId,
    input.cometId,
    String(input.generation),
    input.accountId,
    String(input.batch),
  ].join(SEPARATOR);
}

/**
 * **O id da aquisição de uma joia**, derivado da GERAÇÃO e não do golpe.
 *
 * Uma geração contém no máximo uma joia, portanto este id é único por geração —
 * e é isso que faz a aquisição ser irrepetível mesmo que a sala pergunte duas
 * vezes. A conta não entra: se entrasse, dois jogadores a tentarem no mesmo
 * instante produziriam dois ids diferentes para a mesma joia, e a idempotência
 * deixaria de proteger o que existe para proteger.
 */
export function openWorldCometJewelEventId(input: {
  readonly worldId: string;
  readonly cometId: string;
  readonly generation: number;
}): string {
  return ['comet-jewel', input.worldId, input.cometId, String(input.generation)].join(SEPARATOR);
}

/* -------------------------------------------------------------------------- */
/*  A forma pública                                                           */
/* -------------------------------------------------------------------------- */

/** O id público de um cometa: o id do espaço que ele ocupa. Estável no mundo. */
export const openWorldCometIdSchema = z.string().min(1).max(64);

/**
 * **Um cometa a ENTRAR na área de interesse**: tudo o que não muda.
 *
 * Dito uma vez, como um casco. A posição não muda porque um cometa não se move,
 * e o número de partes com que ele nasceu não muda dentro de uma geração — o
 * que muda é quais delas ainda lá estão, e isso viaja no estado.
 */
export const openWorldCometEntrySchema = z
  .object({
    i: openWorldCometIdSchema,
    /** A geração. Muda quando o cometa renasce, e é o que invalida o desenho. */
    g: z.number().int().min(1),
    /** Onde ele está, em milli-unidades locais do quadrante. */
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    /** O raio do corpo, em milli-unidades. Decide a escala desenhada. */
    r: z.number().int().min(1),
    /** Quantas partes esta geração teve ao nascer. */
    p: z.number().int().min(1).max(32),
    /**
     * A semente VISUAL desta geração.
     *
     * Um inteiro pequeno e público, e é público de propósito: ele decide a
     * silhueta de cada parte e mais nada. Não é a semente do mundo, não deriva
     * a rolagem da joia e não permite inferi-la — dois cometas com a mesma
     * semente visual têm o mesmo aspeto e destinos independentes.
     */
    v: z.number().int().min(0),
  })
  .strict();

export type OpenWorldCometEntry = z.infer<typeof openWorldCometEntrySchema>;

/**
 * **O estado de um cometa**, a cada trama em que ele mudou.
 *
 * Só o que muda: que partes restam, e o núcleo. Um cometa parado não aparece
 * aqui, que é o que faz dez cometas num quadrante custarem quase nada.
 */
export const openWorldCometStateSchema = z
  .object({
    i: openWorldCometIdSchema,
    /**
     * As partes ainda presentes, por índice.
     *
     * Uma máscara de bits: o bit `n` está ligado quando a parte `n` continua
     * inteira. Trinta e duas partes cabem num inteiro, e o cliente desenha o
     * que estiver ligado — nenhuma lista, nenhuma alocação por trama.
     */
    m: z.number().int().min(0),
    /**
     * O núcleo está exposto.
     *
     * Isto é o que a revelação publica, e é tudo o que ela publica. Um núcleo
     * exposto num cometa sem joia tem exatamente esta forma.
     */
    c: z.boolean(),
    /** A vida pública do núcleo em milésimos do total, 0..1000. Zero sem núcleo exposto. */
    ch: z.number().int().min(0).max(1_000),
  })
  .strict();

export type OpenWorldCometState = z.infer<typeof openWorldCometStateSchema>;

/** O que aconteceu a um cometa, para o cliente desenhar uma vez e esquecer. */
export const OPEN_WORLD_COMET_EVENTS = [
  /** Um golpe aterrou numa parte. */
  'impact',
  /** Uma parte partiu-se. */
  'fragment',
  /** O núcleo ficou exposto. */
  'reveal',
  /** A joia foi levada. */
  'acquired',
  /**
   * **A carga estava cheia**, e por isso a joia nao foi levada.
   *
   * Dito porque tem de ser visivel: o nucleo continua aceso, a joia continua
   * la, e sem uma palavra o jogador via o golpe a nao fazer nada. Nao revela o
   * que a joia e — so que havia uma e que nao coube.
   */
  'full',
] as const;

export type OpenWorldCometEventKind = (typeof OPEN_WORLD_COMET_EVENTS)[number];

/**
 * **Um evento de cometa**, deduplicável pela sua própria identidade.
 *
 * O `n` é um ordinal que só sobe dentro de uma geração: um cliente que receba a
 * mesma trama duas vezes desenha o efeito uma vez. Não é um relógio e não é uma
 * posição numa lista — é o número do evento.
 */
export const openWorldCometEventSchema = z
  .object({
    i: openWorldCometIdSchema,
    /** O ordinal do evento dentro desta geração. Estritamente crescente. */
    n: z.number().int().min(1),
    k: z.enum(OPEN_WORLD_COMET_EVENTS),
    /** Que parte, quando o evento é de uma parte. `-1` para o corpo inteiro. */
    p: z.number().int().min(-1).max(31),
    /** Onde, em milli-unidades locais. O ponto que o cliente desenha. */
    x: z.number().int().min(0),
    y: z.number().int().min(0),
  })
  .strict();

export type OpenWorldCometEvent = z.infer<typeof openWorldCometEventSchema>;

/** Um cometa que SAIU da área de interesse, ou que deixou de existir. */
export const openWorldCometGoneSchema = openWorldCometIdSchema;
